<template>
  <div class="patient-core-fields" :class="{ compact: compact }">
    <div class="personal-info-form-content">
      <h3 v-if="!hideHeadings">Personal Information</h3>
      <div class="form-fields">
        <div class="form-field text-field">
          <label for="first_name"
            >First Name <span style="color: red">*</span></label
          >
          <input
            type="text"
            name="first_name"
            placeholder="Enter First Name"
            :value="personalInformation.first_name"
            @input="setPersonal('first_name', $event.target.value)"
          />
        </div>
        <div class="form-field text-field">
          <label for="last_name"
            >Last Name <span style="color: red">*</span></label
          >
          <input
            type="text"
            name="last_name"
            placeholder="Enter Last Name"
            :value="personalInformation.last_name"
            @input="setPersonal('last_name', $event.target.value)"
          />
        </div>
        <div class="form-field text-field" v-if="showPreferredName">
          <label for="preferred_name">Preferred Name </label>
          <input
            type="text"
            name="preferred_name"
            :value="personalInformation.preferred_name"
            @input="setPersonal('preferred_name', $event.target.value)"
          />
        </div>
        <div
          v-if="similarSoundingName"
          style="margin-top: 2em; margin-bottom: 2em; width: 100%"
        >
          <span
            style="font-size: 1em; font-weight: bold; color: var(--red-500, red)"
            >This {{ $resident() }} has a similar sounding name to another
            {{ $resident() }}.</span
          >
        </div>
        <div class="form-field text-field">
          <label for="sex"
            >Sex <span v-if="!isSimplified" style="color: red">*</span></label
          >
          <Dropdown
            :modelValue="personalInformation.sex"
            @update:modelValue="setPersonal('sex', $event)"
            :options="sexOptions"
            optionLabel="sex"
            optionValue="sex"
            :placeholder="
              personalInformation.sex ? personalInformation.sex : 'Select sex'
            "
          />
        </div>
        <div class="form-field date-field">
          <label for="dob"
            >Date of Birth<span v-if="!isSimplified" style="color: red"
              >*</span
            ></label
          >
          <input
            type="date"
            name="dob"
            :value="personalInformation.dob"
            @input="setPersonal('dob', $event.target.value)"
          />
        </div>

        <p class="entitlement-hint">
          Medicare, DVA and IHI Numbers are optional. A Medicare Number that is
          entered has to be valid and carry an expiry date that has not passed,
          or the patient cannot be saved - clear the number to save without it.
        </p>
        <div class="form-field text-field" style="position: relative">
          <label for="medicare_number">Medicare Number</label>
          <InputText
            class="p-inputtext p-component"
            style="background-color: #f3f4f4; width: 100%"
            :modelValue="medicareNumber"
            @update:modelValue="setMedicareNumber($event)"
            @blur="medicareTouched = true"
            placeholder="9999 99999 9 / 9(IRN)"
          />
          <small
            v-show="
              medicareNumber &&
              (medicareTouched ||
                medicareNumber.replace(/\D/g, '').length >= 10) &&
              !validMedicareNumber
            "
            class="patient-core-fields-error"
            >Invalid Medicare Number format.</small
          >
        </div>
        <div class="form-field text-field" style="position: relative">
          <label for="medicare_expiry">Medicare Expiry</label>
          <Calendar
            :id="compact ? 'compactmonthpicker' : 'monthpicker'"
            :modelValue="regulatoryNumbers.medicare_number.expiry"
            @update:modelValue="setRegulatory('medicare_number', 'expiry', $event)"
            view="month"
            dateFormat="mm/yy"
            :yearNavigator="true"
            yearRange="2020:2050"
            style="width: 100%"
            inputStyle="background-color: #f3f4f4; width: 100%;"
          />
          <small
            v-show="
              regulatoryNumbers.medicare_number.expiry && !validMedicareExpiry
            "
            class="patient-core-fields-error"
            >Expiry date must be in the future.</small
          >
          <small
            v-show="medicareExpiryRequired"
            class="patient-core-fields-error"
            >Expiry is required with a Medicare Number.</small
          >
        </div>
        <div class="form-field text-field" style="position: relative">
          <label for="dva_number">DVA Number</label>
          <input
            type="text"
            style="width: 100%"
            :value="regulatoryNumbers.dva_number.number"
            @input="setRegulatory('dva_number', 'number', $event.target.value)"
          />
          <small
            v-show="regulatoryNumbers.dva_number.number && !validDVANumber"
            class="patient-core-fields-error"
            >Invalid DVA Number format.</small
          >
        </div>
        <div class="form-field text-field" v-if="showDvaDetails">
          <label for="dva_status">DVA Status </label>
          <Dropdown
            :modelValue="regulatoryNumbers.dva_number.regulatory_type"
            @update:modelValue="
              setRegulatory('dva_number', 'regulatory_type', $event)
            "
            :options="dvaStatuses"
            optionLabel="status"
            optionValue="value"
            placeholder="Select DVA Status"
          />
        </div>
        <div
          class="form-field text-field"
          v-if="showDvaDetails"
          style="position: relative"
        >
          <label for="dva_expiry">DVA Expiry</label>
          <Calendar
            :id="compact ? 'compactdvamonthpicker' : 'dvamonthpicker'"
            :modelValue="regulatoryNumbers.dva_number.expiry"
            @update:modelValue="setRegulatory('dva_number', 'expiry', $event)"
            view="month"
            dateFormat="mm/yy"
            :yearNavigator="true"
            yearRange="2020:2050"
            style="width: 100%"
            inputStyle="background-color: #f3f4f4; width: 100%;"
          />
          <small
            v-show="regulatoryNumbers.dva_number.expiry && !validDVAExpiry"
            class="patient-core-fields-error"
            >Expiry date must be in the future.</small
          >
          <small
            v-show="dvaExpiryRequiresNumber"
            class="patient-core-fields-error"
            >DVA Number is required to save DVA Expiry.</small
          >
        </div>
        <!-- IHI Number Fields -->
        <div
          class="personal-info-form-content"
          style="margin-bottom: 1em"
          v-if="showIhi && ihiDetails && $store.state.user.eprescribing_active"
        >
          <h3>
            IHI Number
            <span
              v-if="ihiDetails.last_checked"
              style="font-size: 0.85em; font-weight: normal; margin-left: 10px"
              >(Last checked: {{ ihiDetails.last_checked }})</span
            >
          </h3>
          <p style="margin: 0; font-size: 0.9em; text-align: left">
            Optional. An IHI Number is needed for electronic prescribing, and
            can be entered here or retrieved later. To retrieve it, fill in the
            {{ $resident() }}'s name, sex, DOB and Medicare or DVA Number.
          </p>
          <div class="form-fields">
            <div class="form-field text-field">
              <label for="ihi_number">IHI Number</label>
              <InputMask
                style="background-color: #f3f4f4"
                mask="9999 9999 9999 9999"
                :modelValue="ihiDetails.number"
                @update:modelValue="setIhiNumber($event)"
                placeholder="9999 9999 9999 9999"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Location -->
    <div class="personal-info-form-content">
      <h3 v-if="!hideHeadings" :style="locationHeadingStyle">
        {{ $resident({ capitalize: true }) }} Location
      </h3>
      <div class="form-fields" v-if="!facilities || !facilities.length">
        <div class="form-field text-field">
          <p style="font-size: 1em; margin-left: 1em">
            Loading location data...
          </p>
        </div>
      </div>
      <div v-else class="form-fields" style="align-items: flex-end">
        <div class="form-field text-field">
          <label for="facility"
            >Facility <span style="color: red">*</span></label
          >
          <Dropdown
            :modelValue="selectedFacility"
            @update:modelValue="onFacilityChange"
            :options="facilities"
            optionLabel="label"
            :placeholder="
              selectedFacility ? selectedFacility.name : 'Select facility'
            "
          />
        </div>
        <div class="form-field text-field" v-if="loadingLocations">
          <label for="location"
            >Location <span style="color: red">*</span></label
          >
          <p style="font-size: 0.9em; color: #888; margin-top: 5px">
            Loading locations...
          </p>
        </div>
        <div class="form-field text-field" v-else-if="nodes && nodes.length">
          <label for="location"
            >Location <span style="color: red">*</span></label
          >
          <TreeSelect
            :modelValue="selectedNodeKey"
            @update:modelValue="$emit('update:selectedNodeKey', $event)"
            :options="nodes"
            ariaLabelledBy="name"
            placeholder="Select Item"
          />
        </div>
        <div
          class="form-field text-field"
          v-else-if="selectedFacility && !loadingLocations"
        >
          <label for="location"
            >Location <span style="color: red">*</span></label
          >
          <p style="font-size: 0.9em; color: #888">
            No locations found for this facility.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  SEX_OPTIONS,
  DVA_STATUSES,
  formatMedicareNumber,
  validateMedicareNumber,
  validateDVANumber,
  validateExpiryInFuture,
} from "@/services/patientValidation";

// Shared personal-information + regulatory + location block for the patient
// create flows. Both the nav "Create New Patient" view and the patient search
// modal render this so the two can't drift apart again.
//
// The parent owns the state: every edit emits a replacement object rather than
// mutating the prop, and location selection is emitted back because the parent
// owns the facility -> locations fetch.
export default {
  name: "PatientCoreFields",
  props: {
    personalInformation: { type: Object, required: true },
    regulatoryNumbers: { type: Object, required: true },
    ihiDetails: { type: Object, default: null },
    facilities: { type: Array, default: () => [] },
    nodes: { type: Array, default: () => [] },
    selectedFacility: { type: Object, default: null },
    selectedNodeKey: { type: [Object, String], default: null },
    loadingLocations: { type: Boolean, default: false },
    isSimplified: { type: Boolean, default: false },
    similarSoundingName: { type: Boolean, default: false },
    // Narrow two-column layout for use inside a modal.
    compact: { type: Boolean, default: false },
    hideHeadings: { type: Boolean, default: false },
    showPreferredName: { type: Boolean, default: true },
    showDvaDetails: { type: Boolean, default: true },
    showIhi: { type: Boolean, default: true },
  },
  emits: [
    "update:personalInformation",
    "update:regulatoryNumbers",
    "update:ihiDetails",
    "update:selectedFacility",
    "update:selectedNodeKey",
    "facility-change",
  ],
  data() {
    return {
      medicareTouched: false,
      sexOptions: SEX_OPTIONS,
      dvaStatuses: DVA_STATUSES,
    };
  },
  methods: {
    setPersonal(key, value) {
      this.$emit("update:personalInformation", {
        ...this.personalInformation,
        [key]: value,
      });
    },
    setRegulatory(group, key, value) {
      this.$emit("update:regulatoryNumbers", {
        ...this.regulatoryNumbers,
        [group]: { ...this.regulatoryNumbers[group], [key]: value },
      });
    },
    setMedicareNumber(value) {
      this.setRegulatory(
        "medicare_number",
        "number",
        formatMedicareNumber(value),
      );
    },
    setIhiNumber(value) {
      this.$emit("update:ihiDetails", { ...this.ihiDetails, number: value });
    },
    onFacilityChange(facility) {
      this.$emit("update:selectedFacility", facility);
      this.$emit("facility-change", facility);
    },
  },
  computed: {
    medicareNumber() {
      return this.regulatoryNumbers.medicare_number.number || "";
    },
    validMedicareNumber() {
      return validateMedicareNumber(this.medicareNumber);
    },
    validMedicareExpiry() {
      return validateExpiryInFuture(
        this.regulatoryNumbers.medicare_number.expiry,
      );
    },
    validDVANumber() {
      return validateDVANumber(this.regulatoryNumbers.dva_number.number);
    },
    validDVAExpiry() {
      return validateExpiryInFuture(this.regulatoryNumbers.dva_number.expiry);
    },
    dvaExpiryRequiresNumber() {
      return (
        !!this.regulatoryNumbers.dva_number.expiry &&
        !this.regulatoryNumbers.dva_number.number
      );
    },
    medicareExpiryRequired() {
      return (
        !!this.regulatoryNumbers.medicare_number.number &&
        !this.regulatoryNumbers.medicare_number.expiry
      );
    },
    locationHeadingStyle() {
      const p = this.personalInformation;
      const incomplete =
        !p.first_name ||
        !p.last_name ||
        (!this.isSimplified && (!p.dob || !p.sex));
      return incomplete ? { color: "grey" } : {};
    },
  },
};
</script>

<style lang="scss">
// These layout rules used to live in NewPatient.vue's global style block, so
// they only existed once that view had been loaded. They ship with the
// component now so the modal renders identically on its own.
.patient-core-fields {
  .form-fields {
    display: flex;
    flex-wrap: wrap;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    text-align: left;
    margin-right: 20px;
    margin-top: 15px;

    label {
      padding-left: 10px;
    }

    input,
    label {
      display: block;
    }
  }

  .text-field,
  .date-field {
    width: 20em;
  }

  h3 {
    text-align: left;
    margin-bottom: 0;
    font-size: 18px;
  }

  .entitlement-hint {
    width: 100%;
    text-align: left;
    margin: 15px 0 0 0;
    font-size: 0.9em;
    color: #666;
  }
}

.patient-core-fields-error {
  color: red;
  position: absolute;
  bottom: -18px;
  left: 0;
  white-space: nowrap;
}

// Modal layout: two even columns that fill the available width.
.patient-core-fields.compact {
  .form-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 1em;
    align-items: end;
  }

  .form-field {
    margin-right: 0;
    margin-bottom: 8px;
  }

  .text-field,
  .date-field {
    width: auto;
    min-width: 0;
  }

  input:not([type="checkbox"]),
  .p-inputtext,
  .p-dropdown,
  .p-treeselect,
  .p-calendar {
    width: 100%;
  }

  .patient-core-fields-error {
    position: static;
    display: block;
    margin-top: 2px;
    white-space: normal;
  }

  .entitlement-hint {
    grid-column: 1 / -1;
    margin-top: 8px;
  }
}

#compactmonthpicker,
#compactdvamonthpicker {
  border-radius: 12px;

  input.p-inputtext {
    background-color: #f3f4f4 !important;
  }
}
</style>
